import debounce from 'lodash/debounce';
import get from 'lodash/get';
import moment from 'moment';
import omit from 'lodash/omit';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import {useCallback,useEffect,useState} from 'react';
import PropTypes from 'prop-types';
import {roundTo} from 'round-to';
import {connect} from 'react-redux';
import {useSearchParams} from 'react-router-dom';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CheckIcon from '@mui/icons-material/Check';
import CircleIcon from '@mui/icons-material/Circle';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import {getStates} from '../../helpers/selectOptions';
import CompleteCaseForm from '../cases/CompleteCaseForm';
import CustomNumberField from '../common/CustomNumberField';
import CustomSelect from '../common/CustomSelect';
import CustomTextField from '../common/CustomTextField';
import FreeTextNote from '../patients/FreeTextNote';
import SessionSummary from './SessionSummary';
import CPTSelect from './CPTSelect';
import BillingCalculator from './BillingCalculator';
import LoadingScreen from '../common/LoadingScreen';
import * as analytics from '../../redux/reducers/analytics';
import {useGetICD10CodesQuery} from '../../redux/services/codeApi';
import {
  useGetSessionNotesQuery,
  useUpdateSessionNoteMutation
} from '../../redux/services/sessionNoteApi';
import { set } from 'lodash';

const sections = [
  {
    number: 1,
    value: 'planOfCare',
    label: 'Plan of Care',
  },
  {
    number: 2,
    value: 'note',
    label: 'Note',
  },
  {
    number: 3,
    value: 'billing',
    label: 'Billing',
  },
  {
    number: 4,
    value: 'details',
    label: 'Session Details',
  },
];

const getFields = ({diagnosisCodes} = {}) => [
  {
    label: 'Summary',
    name: 'summary',
    type: 'textarea',
    rows: 5,
    section: 'summary',
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    allowedInAddendum: true,
  },
  {
    label: 'ICD-10 Diagnosis',
    name: 'code_diagnosis',
    type: 'select',
    section: 'planOfCare',
    helperText: null,
    placeholder: null,
    required: false,
    // maxWidth: '400px',
    validate: () => true,
    options: (
      (diagnosisCodes && diagnosisCodes.length ? diagnosisCodes : [])
      .map(c => ({
        value: c.code,
        label: `${c.code} ${c.description}`,
      }))
    ),
    multiple: true,
    allowedInAddendum: true,
    filterSelectedOptions: true,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Frequency/duration',
    name: 'poc_duration',
    type: 'text',
    section: 'planOfCare',
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    allowedInAddendum: true,
    grid: {
      xs: 12,
      md: 6
    }
  },
  {
    label: 'Long-term goals',
    name: 'poc_goals',
    type: 'textarea',
    section: 'planOfCare',
    rows: 4,
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    allowedInAddendum: true,
  },
  {
    label: 'Subjective',
    name: 'comment_subjective',
    type: 'textarea',
    section: 'note',
    rows: 6,
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    allowedInAddendum: true,
  },
  {
    label: 'Objective',
    name: 'comment_objective',
    type: 'textarea',
    section: 'note',
    rows: 6,
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    allowedInAddendum: true,
  },
  {
    label: 'Treatment',
    name: 'comment_treatment',
    type: 'textarea',
    section: 'note',
    rows: 6,
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    allowedInAddendum: true,
  },
  {
    label: 'Assessment',
    name: 'comment_assessment',
    type: 'textarea',
    section: 'note',
    rows: 6,
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    allowedInAddendum: true,
  },
  {
    label: 'This is the last session in this plan of care.',
    name: 'case_status',
    type: 'caseStatus',
    section: 'note',
    rows: 12,
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    allowedInAddendum: true,
  },
  {
    label: 'Plan',
    name: 'comment_interventions',
    type: 'textarea',
    section: 'note',
    rows: 6,
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    allowedInAddendum: true,
  },
  {
    label: 'CPT Codes',
    name: 'cpt_codes',
    type: 'procedureCodes',
    section: 'billing',
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    allowedInAddendum: true,
  },
  {
    label: 'Place of Service',
    name: 'session_place_service',
    type: 'select',
    section: 'details',
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    options: [
      {value: '02', label: '02 - Telehealth'},
      {value: '03', label: '03 - School'},
      {value: '11', label: '11 - Office'},
      {value: '12', label: '12 - Home'},
      {value: '09', label: '09 - Prison/Correctional Facility'},
      {value: '14', label: '14 - Group Home'},
      {value: '15', label: '15 - Mobile Unit'},
      {value: '16', label: '16 - Temporary Lodging'},
      {value: '01', label: '01 - Walk-in Retail Health Clinic'},
      {value: '18', label: '18 - Place of Employment - Worksite'},
      {value: '25', label: '25 - Birthing Center'},
      {value: '49', label: '49 - Independent Clinic'},
      {value: '71', label: '71 - Public Health Clinic'},
      {value: '72', label: '72 - Rural Health Clinic'},
    ],
    allowedInAddendum: true,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Total Treatment Time (minutes)',
    name: 'session_duration',
    type: 'number',
    min: 0,
    max: 999,
    step: 1,
    maxLength: 3,
    section: 'details',
    helperText: null,
    placeholder: null,
    required: false,
    validate: str => !!str ? /^[\d]{1,3}$/.test(str) : true,
    allowedInAddendum: false,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Discount Percent (0-100)',
    name: 'session_discount',
    type: 'text',
    section: 'details',
    billingTypes: ['CASH', undefined, null], // hide on insurance cases
    helperText: null,
    placeholder: null,
    required: false,
    validate: str => {
      if (!str) return true;
      const num = parseFloat(str);
      if (isNaN(num)) return false;
      if (num > 100 || num < 0) return false;
      const strNum = num.toString();
      // if (!/\.{0,3}$/.test(strNum)) return false;
      return true;
    },
    allowedInAddendum: false,
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'State',
    name: 'session_state',
    type: 'select',
    section: 'details',
    helperText: null,
    required: false,
    validate: str => true,
    allowedInAddendum: true,
    options: getStates(),
    grid: {
      xs: 12,
      md: 6
    },
  },
  {
    label: 'Addendum Reason',
    name: 'addendum_reason',
    type: 'textarea',
    multi: true,
    rows: 3,
    section: 'details',
    helperText: null,
    placeholder: null,
    required: false,
    validate: () => true,
    allowedInAddendum: true,
  },
];

const defaultFormValues = getFields().reduce((acc, next) => ({
  ...acc,
  [next.name]: (
    next.type === 'options' || next.multiple | next.type === 'procedureCodes'
    ? []
    : ''
  ),
}), {});

const handleFieldUpdate = ({formValues, setFormValues, setSaved}) => (
  fieldName => e => {
    let value = get(e, 'target.value', '');
    // ignore free text inputs from the code_diagnosis select
    // console.log('handleFieldUpdate', fieldName, value);
    if (fieldName === 'code_diagnosis' && isString(value)) return;
    // const inputType = get(getFields().find(f => f.name === fieldName), 'type');
    // if (inputType === 'options') value = value.map(opt => opt.value);
    const values = {
      ...formValues,
      [fieldName]: value,
    };
    setFormValues(values);
    setSaved(false);
  }
);

// eslint-disable-next-line no-unused-vars
const checkIfFormIsValid = (formValues, validationDict) => {
  const containsInvalidValues = Object.keys(validationDict).reduce((acc, k) => (
    validationDict[k] ? acc : false
  ), true);
  return containsInvalidValues;
};

const parseFormValues = isComplete => formValues => {
  const fields = getFields();
  const addendumFields = [
    ...fields
      .filter(f => f.allowedInAddendum)
      .map(f => f.name),
    'session_id',
  ];
  const parsedForm = Object.keys(formValues).reduce((acc, k) => {
    if (isComplete && !addendumFields.includes(k)) return acc;
    const field = fields.find(f => f.name === k);
    const inputType = get(field, 'type');
    let value = (
      inputType === 'options' && field.multiple
      ? (
        formValues[k]
        ? formValues[k].map(opt => opt.value ? opt.value : opt)
        : []
      )
      : get(formValues, k)
    );
    if (k === 'code_diagnosis') {
      value = (value || []).map(v => v.value ? v.value : v);
      // console.log('code_diagnosis.VALUE', value);
    }
    if (k === 'cpt_codes') {
      value = (value || []).filter(c => c.code);
    }
    if (k === 'session_discount') {
      value = value ? roundTo(parseFloat(value), 8) : 0;
    }
    return {...acc, [k]: value};
  }, {});
  return parsedForm;
};

const FieldInput = props => {
  const [blurred, setBlurred] = useState(false);
  let childProps = {
    ...props,
    error: !props.validate(props.value) && blurred,
    onBlur: () => setBlurred(true),
  };
  switch (props.type) {
    case 'text':
      return <CustomTextField fullWidth={true} {...childProps} />;
    case 'textarea':
       return <CustomTextField fullWidth={true} {...childProps} multiline={true} />;
    case 'number':
      return <CustomNumberField fullWidth={true} type='number' {...childProps} sx={{input: {padding: '11px 14px'}}} />;
    case 'select':
      return <CustomSelect fullWidth={true} {...childProps} />;
    case 'procedureCodes':
      return <CPTSelect {...childProps}/>;
    case 'caseStatus':
      return <CompleteCaseForm {...childProps} />;
    default:
      return <div></div>;
  }
};

const validateSubmission = (body, isCompleted) => {
  if (body.summary) return {isValid: true};
  if (!isNumber(body?.session_duration) && !isCompleted) {
    return {isValid: false, error: new Error('Invalid Session Duration')};
  }
  const diagnosisCodes = body?.code_diagnosis;
  if (!diagnosisCodes || !diagnosisCodes.length) {
    return {isValid: false, error: new Error('Missing Diagnosis Codes')};
  }
  const procedureCodes = body?.cpt_codes;
  if (!procedureCodes || !procedureCodes.length) {
    return {isValid: false, error: new Error('Missing Procedure Codes')};
  }
  const requiredStringKeys = [
    'poc_duration',
    'poc_goals',
    'comment_subjective',
    'comment_objective', 
    'comment_treatment', 
    'comment_assessment', 
    'comment_interventions',
    'session_place_service',
    'session_state',
  ];
  const validation = requiredStringKeys.reduce((acc, k) => {
    if (!acc.isValid) return acc;
    if (!body?.[k]) return {isValid: false, error: new Error(`Missing ${k}`)};
    if (body[k] === '') return {isValid: false, error: new Error(`Missing ${k}`)};
    return acc;
  }, {isValid: true, error: null});
  return validation;
};

const handleSave = ({
  session,
  updateNote,
  setSaving,
  setError,
  setSaved,
  setAutoSaveOn,
  onSubmitSuccess,
  track,
}) => async ({complete = false, saved, formValues} = {}) => {
  if (saved && !complete) return;
  setSaving(true);
  setError(null);
  const isCompleted = get(session, 'is_completed') || false;
  let body = parseFormValues(isCompleted)({
    ...formValues,
    session_id: session.id,
    finalize: complete,
  });
  if (isCompleted) {
    body = omit( body, 'session_discount');
  } else {
    body = omit(body, 'addendum_reason');
  }
  const {isValid, error} = (
    complete || isCompleted
    ? validateSubmission(body, isCompleted) 
    : {isValid: true, error: null}
  );
  if (!isValid) {
    setSaving(false);
    setAutoSaveOn(!complete);
    return setError(error);
  }
  const response = await updateNote(body);
  if (track && complete) {
    track({
      eventName: 'sessionNote.submit',
      // eventName: complete ? 'sessionNote.submit' : 'sessionNote.addendumSubmit', 
      session_id: session?.id,
      patient_id: session?.patient?.id,
      provider_id: session?.provider?.id,
      state: session?.provider?.address_state,
      city: session?.provider?.address_city,
      zip_code: session?.provider?.address_zip,
      is_completed: isCompleted,
    });
  }
  setSaving(false);
  setAutoSaveOn(!complete);
  if (response.error) {
    const message = get(response, 'error.data.errors[0]', 'Unknown Error');
    track({eventName: 'sessionNote.submitError', message})
    return setError(new Error(message));
  }
  setError(null);
  setSaved(true);
  if (complete && onSubmitSuccess) onSubmitSuccess();
};

const checkIfSubmittable = (session) => {
  let doNotSubmit = false;
  let doNotSubmitReason = '';
  if (moment().isBefore(moment(session.date))) {
    doNotSubmit = true;
    doNotSubmitReason = 'You cannot submit notes for sessions in the future.';
  }
  if (session?.case?.payer_plan && !session?.case?.payer_plan?.is_active) {
    doNotSubmit = true;
    doNotSubmitReason = 'You cannot submit this note because the case\'s payer plan is inactive!';
  }
  return {doNotSubmit, doNotSubmitReason};
};

const SessionNoteForm = props => {
  // eslint-disable-next-line no-unused-vars
  const {diagnosisCodes, updateNote, sessionNote, session, autosave: onAutoSave} = props;
  const defaultNote = props?.sessionNote || props?.priorNote;
  let defaultValues = {
    ...defaultNote,
    code_diagnosis: (
      get(defaultNote, 'code_diagnosis', '')
      ? get(defaultNote, 'code_diagnosis', '').replace(/\s/g, '').split(',')
      : null
    ),
    session_duration: get(props, 'session.duration'),
    session_discount: (
      (!props?.session?.case || props.session.case.billing_category !== 'DIRECT_BILL')
      ? (
        props?.sessionNote
        ? get(props, 'session.discount')
        : get(props, 'priorNote.session.discount')
      ) : null
    ),
    session_place_service: get(props, 'session.place_service') || get(props, 'priorNote.session.place_service'),
    session_state: get(props, 'session.state'),
  } || defaultFormValues;
  // console.log('defaultValues', defaultValues);
  // console.log('session', session);
  // console.log('sessionNote', sessionNote);
  // console.log('priorNote', props.priorNote);
  const [autoSaveOn, setAutoSaveOn] = useState(!session.is_completed);
  const [formValues, setFormValues] = useState(defaultValues);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(true);
  const [error, setError] = useState(null);
  const save = handleSave({
    formValues,
    session,
    updateNote,
    setSaving,
    setError,
    setSaved,
    setAutoSaveOn,
    onSubmitSuccess: props?.onSubmitSuccess,
    track: props.track,
  });
  const shouldAutoSave = !(
    saving
    || !autoSaveOn
    || !session
    || session.is_completed
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(debounce(save, props.autosaveDelay), [save])
  useEffect(() => {
    if (shouldAutoSave) {
      // console.log('autosaving', formValues);
      debouncedSave({formValues, complete: false, saved});
    }
  }, [debouncedSave, formValues, saved, shouldAutoSave]);
  const formFields = getFields({diagnosisCodes});
  const isOptimize = get(props, 'session.category') === 'optimize';
  const _sections = (
    isOptimize
    ? [
        {number: 1, value: 'summary', label: 'Summary'},
        {number: 2, value: 'details', label: 'Session Details'},
    ]
    : sections
  );
  const onFieldUpdate = handleFieldUpdate({
    formValues,
    setFormValues,
    setSaved,
    // onAutosave: handleAutosave({saving, onSave, autoSaveOn, session, formValues}),
  });
  const isCompleted = props?.session?.is_completed || false;
  const title = isCompleted ? 'Addendum Note' : 'Edit Note';
  const rate = isNumber(session.rate) ? session.rate : session.provider?.rate;
  const copay = session?.copay || session?.case?.payer_plan?.copay;
  const {doNotSubmit, doNotSubmitReason} = checkIfSubmittable(session);
  return (
    <Box>
      <Grid container spacing={2} padding={2}>
        {
          props.session
          ? <Grid item xs={12} md={12}>
              <SessionSummary session={props.session} showBillingInstructions={true}/>
            </Grid>
          : null
        }
        {
          props?.session?.case && props?.session.category === 'optimize' &&
            <Grid item xs={12} md={12}>
              <Alert severity='warning'>
                Optimize sessions cannot be billed to insurance - by submitting this note, you 
                acknowledge this session will not be billed to the payer and the patient will 
                be charged for the total below. This may apply to drive time fees or cancelled 
                session fees.  If this case no longer is billing insurance, please create a new case.
              </Alert>
            </Grid>
        }
        <Grid item xs={12} md={12} style={{paddingTop: 0}}>
          {
            saved
              ? <Stack spacing={1} direction='row' alignItems='center' justifyContent='flex-end'>
                <CheckIcon style={{color: config().colors.success}}/>
                <Typography variant='body' style={{color: config().colors.success}}>
                  Saved
                </Typography>
              </Stack>
            : <Stack spacing={1} direction='row' alignItems='center' justifyContent='flex-end'>
              <CircleIcon style={{color: config().colors.secondary}}/>
              <Typography variant='body' style={{color: config().colors.secondary}}>
                Unsaved Changes
              </Typography>
            </Stack>
          }
        </Grid>
        <Grid item xs={12}>
          <FreeTextNote fullWidth patientId={session?.patient?.id}/>
        </Grid>
        <Grid item xs={12}>
          {_sections.map(s =>
              <Grid container spacing={1} style={{paddingBottom: '1.5rem'}}>
                <Grid item xs={12}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Avatar
                      sx={{width: 25, height: 25, bgcolor: config().colors.primary, fontSize: '0.9rem'}}
                    >
                      {s.number}
                    </Avatar>
                    <Typography variant='paragraphMedium'>{s.label}</Typography>
                  </Stack>
                </Grid>
                  {formFields
                    .filter(f =>
                      f.section === s.value
                      && (f.name === 'addendum_reason' ? isCompleted : true)
                    )
                    .filter(f => props?.editableFields ? props.editableFields.includes(f.name) : true)
                    .filter(f => 
                      f.billingTypes
                      ? (
                        f.billingTypes.includes(session?.case?.billing_category)
                        || session.category === 'optimize'
                      )
                      : true
                    )
                    .map(f =>
                      isCompleted && !f.allowedInAddendum
                      ? null
                      : <Grid item xs={f.grid?.xs ?? 12} sm={f.grid?.sm} md={f.grid?.md} style={{paddingTop: 0}}>
                        <FieldInput
                          hidden={isCompleted ? !f.allowedInAddendum : false}
                          defaultValue={get(defaultValues, f.name) || ''}
                          onChange={onFieldUpdate(f.name)}
                          caseId={session?.case?.id}
                          {...f}
                        />
                      </Grid>
                    )
                  }
              </Grid>
          )}
          {
            isCompleted || !isNumber(rate)
            ? null
            : <BillingCalculator 
              session={session} 
              rate={rate} 
              copay={copay}
              formValues={formValues}
            />
          }
        </Grid>
      </Grid>
      {
        error
        ? <Alert severity='error'>
          <AlertTitle>Error Saving Data</AlertTitle>
          {error?.message || 'Unknown Error'}
        </Alert>
        : null
      }
      <Stack spacing={1} padding={2}>
        <Typography color='text.light' variant='body1'>
          By pressing submit I am electronically submitting my signature for this
          note as the provider for this episode of care.
        </Typography>
        <Stack direction='row' alignItems='center' justifyContent='center' spacing={2} padding='1.5rem 0'>
          {
            isCompleted
            ? null
            : <Button
              variant='primaryOutline'
              disabled={saving || saved}
              onClick={() => save({complete: false, saved, formValues})}
            >
              {
                saved
                ? <><CheckIcon style={{ width: '1rem', height: '1rem', marginRight: '0.25rem'}}/>Saved</>
                : 'Save Draft'
              }
            </Button>
          }
          <Button
            disabled={saving || doNotSubmit}
            variant='primaryGradient'
            onClick={() => save({
              complete: true,
              saved: false,
              formValues,
            })}
          >
            Submit
          </Button>
        </Stack>
        {
            doNotSubmit
            && <Alert severity='error'>
              <Typography variant='body1' color='text.light' align='center'>
                {doNotSubmitReason}
              </Typography>
            </Alert>
        }
      </Stack>
    </Box>
  );
};

const SessionNoteFormContainer = props => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = get(props, 'session.id');
  const noteQuery = useGetSessionNotesQuery(
    {session_id: sessionId, latest: true},
    {skip: !sessionId}
  );
  const note = get(noteQuery, 'data.data[0]');
  const priorNoteQuery = useGetSessionNotesQuery(
    {session_id: sessionId, latest_completed: true},
    {skip: !sessionId || noteQuery.isLoading || note}
  );
  const icd10Res = useGetICD10CodesQuery();
  const diagnosisCodes = get(icd10Res, 'data.data');
  const sessionNote = get(noteQuery, 'data.data[0]');
  const priorNote = get(priorNoteQuery, 'data.data[0]');
  const session = get(props, 'session');
  const isLoading = (
    noteQuery.isLoading
    || noteQuery.isUninitialized
    || icd10Res.isLoading
    || !session
    || priorNoteQuery.isLoading
  );
  const [updateNote] = useUpdateSessionNoteMutation();
  // on first load
  useEffect(() => {
    props.track({
      eventName: 'sessionNote.view',
      session_id: sessionId, 
      is_completed: session.is_completed
    });
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('note_editor', true);
    setSearchParams(newSearchParams);
  }, []);
  // load data before rendering form
  if (isLoading) return <LoadingScreen/>;
  const childProps = {
    ...props,
    updateNote,
    sessionNote,
    priorNote,
    diagnosisCodes,
  };
  return <SessionNoteForm {...childProps}/>;
};

SessionNoteForm.defaultProps = {
  autosaveDelay: 500,
};

SessionNoteForm.propTypes = {
  session: PropTypes.object,
  editableFields: PropTypes.arrayOf(PropTypes.string), // optional
  sessionNote: PropTypes.object,
  priorNote: PropTypes.object,
  onSubmit: PropTypes.func,
  onSaveDraft: PropTypes.func,
};

const mapState = () => ({});

const mapDispatch = d => ({
  track: params => d(analytics.track(params)),
});

export default connect(mapState, mapDispatch)(SessionNoteFormContainer);
